import { FC, useCallback, useContext, useState } from 'react';
import Carousel from './Carousel';
import { useWidgetData } from '../../hooks/useWidgetData.ts';
import { FeedQueryResult } from '../../net/softbox-types.ts';
import { useTranslation } from 'react-i18next';
import { CarouselCardProps } from './CarouselCard/CarouselCard';
import { CarouselItemFactory } from './CarouselItemFactory';
import './CarouselWrapper.scss';
import { useSearchParams } from 'react-router';
import { CommonPageDataContext } from '@pages/Root/ContextWrapper/WithInterest/CommonPageDataContext.ts';
import { useAnalytics } from '@moment/analytics/AnalyticsProvider.tsx';
import { IInterestResponse } from '@pages/Root/services/api/interest/interest-model-definitions.ts';

export interface CarouselWrapperProps {
  feedType?: string;
  trending?: boolean;
}

const CarouselWrapper: FC<CarouselWrapperProps> = ({
  feedType = 'dynamic-news',
  trending = false,
}) => {
  const { i18n, t } = useTranslation();
  const [searchParams] = useSearchParams();
  const context = useContext(CommonPageDataContext);
  const analytics = useAnalytics();
  const [interests, setInterests] = useState<IInterestResponse | null>(null);

  // Process data using the factory
  const processData = useCallback(
    (feedData: FeedQueryResult): CarouselCardProps[] => {
      // Store interests for use in Carousel component
      setInterests(feedData.interests);
      
      const factory = new CarouselItemFactory(
        feedData,
        i18n,
        context.partner || '',
        searchParams,
        analytics
      );
      return factory.createAllItems();
    },
    [feedType, i18n, context.partner, searchParams, analytics]
  );

  // Fetch data using the selected feedType
  const {
    data: items,
    loading,
    error,
    refetch,
  } = useWidgetData<CarouselCardProps>(feedType, processData, false, trending);

  const handleRefetch = useCallback(() => {
    refetch();
  }, [refetch]);

  return (
    <div className="carousel-wrapper" data-testid="carousel-wrapper">
      {/* Show loading state */}
      {loading && (
        <div className="carousel-wrapper__loading">
          <div className="carousel-wrapper__loading-text">
            {t('carouselLoading')}
          </div>
        </div>
      )}

      {/* Show error state */}
      {error && (
        <div className="carousel-wrapper__error">
          <div className="carousel-wrapper__error-text">
            {t('carouselError', { error })}
            <button
              onClick={handleRefetch}
              className="carousel-wrapper__retry-button"
            >
              {t('carouselRetry')}
            </button>
          </div>
        </div>
      )}

      {/* Show carousel content */}
      {!loading && !error && items.length > 0 && interests && (
        <Carousel
          items={items}
          interests={interests}
          slidesPerView={1.5}
          spaceBetween={16}
          className="carousel-wrapper__carousel"
        />
      )}

      {/* No items available */}
      {!loading && !error && items.length === 0 && (
        <div className="carousel-wrapper__empty">
          <div className="carousel-wrapper__empty-text">
            {t('carouselEmpty')}
            <button
              onClick={handleRefetch}
              className="carousel-wrapper__retry-button"
            >
              {t('carouselRetry')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CarouselWrapper;
